import { Button } from "@/components/ui/button";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { useFacilitiesContext } from "@/hooks/useFacilitiesContext";
import { useUnitItemContext } from "@/hooks/useUnitItemContext";
import { useUnitJourneyContext } from "@/hooks/useUnitJourneyContext";
import { translateWithMultipleSources } from "@/lib/translate";
import { FunnelType } from "@/lib/utils";
import { emitUnitEvent } from "@/utils/units/unitEvents";
import React from "react";

interface UnitQuoteButtonProps {
    unitId: string | number;
}

export const QuoteButton = React.forwardRef<
    HTMLButtonElement,
    UnitQuoteButtonProps
>(({ unitId }, ref) => {
    const options = useDisplayOptionContext();
    const unitContext = useUnitItemContext();
    const journeyContext = useUnitJourneyContext();
    const { facilities } = useFacilitiesContext();
    const { unitGroup, selectedPromo } = unitContext;
    const facilityInfo = facilities?.find(
        (facility) => facility.id === unitGroup.representative_unit.facility_id,
    );

    const handleQuoteEvent = () => {
        emitUnitEvent(
            unitId,
            unitGroup.representative_unit.facility_id,
            selectedPromo,
            "inquiry",
        );
    };

    const handleExternalSoftReservation = () => {
        if (facilityInfo && options.softReservationApp?.externalUrl) {
            const translatedUrl = translateWithMultipleSources(
                options.softReservationApp.externalUrl,
                {
                    unitGroup,
                    facility: facilityInfo,
                },
                Number(unitId),
                Number(selectedPromo),
            );
            window.location.href = translatedUrl;
        }
    };

    const activateSoftReservationJourney = () => {
        if (journeyContext && !journeyContext.isOpen) {
            journeyContext.setIsOpen(true);
        }
        journeyContext?.setSelectedUnitId(unitId);
        journeyContext?.setUnitJourneyStep("funnel");
        journeyContext?.setFunnelType(FunnelType.QUOTE);
    };

    const handleSoftReserveClick = () => {
        handleQuoteEvent();

        if (
            options.softReservationApp?.location === "external" &&
            options.softReservationApp?.externalUrl
        ) {
            handleExternalSoftReservation();
        } else {
            activateSoftReservationJourney();
        }
    };

    if (
        options.disableSoftReservation &&
        facilityInfo?.settings.quote_type !== "reserve"
    )
        return null;

    return (
        <Button
            ref={ref}
            variant="secondary"
            data-testid="quoteButton"
            onClick={handleSoftReserveClick}
            className="soft-reservation-btn"
        >
            {options.language?.action?.quoteButton}
        </Button>
    );
});

QuoteButton.displayName = "QuoteButton";
